// Helpers de pontuação de transcrições (project-score). Centraliza as faixas de
// cor da nota (0–10) usadas na aba Avaliação (detail-tab-avaliacao), no Dashboard
// (transcript-dashboard) e na coluna "Nota" do histórico (transcript-history),
// evitando duplicar a mesma regra em 3+ componentes (CLAUDE.md: extrair constantes
// compartilhadas).
//
// Faixas alinhadas aos tokens do AAWZ DS:
//   ≥8 success (verde) · ≥6 await (âmbar) · ≥4 warning (laranja) · <4 danger.
//
// Âmbar é `--await`: `--warning-new` existe só como variável SASS, fora dos mapas
// que o :root percorre (src/design/_vars.sass), então resolve vazio no browser.

// Cinza neutro do DS para nota ausente/inválida. Token real da escala --gray-1..8
// do _vars.sass, sem fallback hex — coerente com os 4 tokens semânticos abaixo.
const MUTED_COLOR = 'var(--gray-5)'

// Normaliza um valor de nota para Number ou null (aceita string/null/undefined).
export const toScore = (value) => {
  // String vazia vira 0 no Number() — sem este guard, score '' renderiza "0,0"
  // em vermelho em vez do traço de nota ausente.
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

// Cor da nota por faixa. Valor inválido/ausente → cinza neutro.
export const scoreColor = (score) => {
  const num = toScore(score)
  if (num === null) return MUTED_COLOR
  if (num >= 8) return 'var(--success)'
  if (num >= 6) return 'var(--await)'
  if (num >= 4) return 'var(--warning)'
  return 'var(--danger)'
}

// Nota formatada com uma casa decimal. Ausente/inválida → o fallback informado
// (default '—', travessão usado nas células de tabela).
export const formatScore = (value, fallback = '—') => {
  const num = toScore(value)
  return num === null ? fallback : num.toFixed(1)
}

// Nota formatada no padrão pt-BR (vírgula decimal) — usada no cabeçalho do modal
// de detalhe, onde a nota aparece como número grande ("8,5"). Não trocamos o
// separador direto no formatScore porque a aba Avaliação e o histórico já
// asseguram o ponto; e não usamos formatNumber de @utils/Format porque ele
// converte null em "0,0", matando o fallback de nota ausente.
export const formatScorePtBr = (value, fallback = '—') => {
  return formatScore(value, fallback).replace('.', ',')
}

// Versão clara (tint sobre branco) da cor da nota — usada como fundo de chip de
// nota e de realces, mantendo a mesma cor da faixa porém suave. Mesmo padrão
// color-mix do anel do gauge de sentimento/avaliação.
export const scoreColorSoft = (value) => {
  return 'color-mix(in srgb, ' + scoreColor(value) + ' 15%, #FFFFFF)'
}

// Hex equivalentes dos tokens da faixa da nota (mesmos valores de _vars.sass —
// manter em sincronia: a mesma nota é pintada por scoreColor e por este hex no
// mesmo bloco da aba Avaliação, e divergir faz a nota sair em duas cores).
// Necessário para gráficos em canvas (chart.js) que NÃO resolvem `var(--x)`.
// Mesma justificativa do SENTIMENT_HEX no gauge de sentimento.
export const scoreColorHex = (value) => {
  const num = toScore(value)
  if (num === null) return '#A79CAC' // gray-5
  if (num >= 8) return '#398E4A' // success
  if (num >= 6) return '#e0c200' // await
  if (num >= 4) return '#FF6433' // warning
  return '#EB5757' // danger
}

// Token de cor da nota por faixa (nome do token do DS, sem o `var(--...)`) — para
// props que esperam o nome do token, como o `color` do BaseStatus. Ausente → ''
// (BaseStatus cai no cinza default). Mesmas faixas de scoreColor.
export const scoreColorToken = (value) => {
  const num = toScore(value)
  if (num === null) return ''
  if (num >= 8) return 'success'
  if (num >= 6) return 'await'
  if (num >= 4) return 'warning'
  return 'danger'
}

// Rótulo/tier da nota por faixa (mesmas faixas de scoreColor) → { label, color }.
// Usado abaixo do gauge da nota na aba Avaliação (ponto colorido + texto).
export const scoreTier = (value) => {
  const num = toScore(value)
  if (num === null) return { label: '—', color: MUTED_COLOR }
  if (num >= 8) return { label: 'Excelente', color: 'var(--success)' }
  if (num >= 6) return { label: 'Bom', color: 'var(--await)' }
  if (num >= 4) return { label: 'Atenção', color: 'var(--warning)' }
  return { label: 'Crítico', color: 'var(--danger)' }
}
